import { Actions, AuthLayout, Button, CheckField, FieldGroup, Notice, TextField } from '@trellistry/ui';
import { useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import { beginRecovery, completeRecovery, finishGrant } from '../api.js';
import { AuthenticatorStep, BackupCodesStep, PasswordStep } from './NewDetails.js';
import { signInFailure } from './sign-in-messages.js';

/**
 * Getting back in (design §5): a code sent by email and a second proof, a backup code or a code from the church's
 * administrator. The email alone never opens an account. Then the person replaces what they lost and signs in. The
 * first answer is the same whether or not the address has an account.
 */
type Stage = 'email' | 'proof' | 'choose' | 'password' | 'authenticator' | 'codes';

export function Recover({ onDone }: { onDone: () => void }) {
  const [stage, setStage] = useState<Stage>('email');
  const [email, setEmail] = useState('');
  const [challengeId, setChallengeId] = useState<string | null>(null);
  const [grantId, setGrantId] = useState<string | null>(null);
  const [emailCode, setEmailCode] = useState('');
  const [proofKind, setProofKind] = useState<'backup' | 'administrator'>('backup');
  const [proof, setProof] = useState('');
  const [codes, setCodes] = useState<string[]>([]);
  const [changed, setChanged] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const startAgain = (message = 'That took a little too long. Please start again.') => {
    setStage('email'); setChallengeId(null); setGrantId(null); setEmailCode(''); setProof(''); setChanged([]); setError(message);
  };

  const guarded = (fn: () => Promise<void>) => async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try { await fn(); } catch (e) { setError(signInFailure(e)); } finally { setBusy(false); }
  };

  const sendCode = guarded(async () => {
    const r = await beginRecovery(email);
    setChallengeId(r.challengeId);
    setStage('proof');
  });

  const checkProof = guarded(async () => {
    const given = proof.trim();
    const r = await completeRecovery(challengeId!, emailCode.replace(/\s/g, ''), proofKind === 'backup' ? { backupCode: given } : { approvalId: given });
    if (r.status === 'granted') { setGrantId(r.grantId); setStage('choose'); }
    else if (r.status === 'failed') setError(r.attemptsLeft === 0 ? 'That’s too many tries. Please start again.' : 'Those codes didn’t match. Please check them and try again.');
    else startAgain();
  });

  const finish = async () => {
    if (grantId) await finishGrant(grantId).catch(() => undefined);
    onDone();
  };

  const chooseProof = (kind: 'backup' | 'administrator') => { setProofKind(kind); setProof(''); };

  return (
    <AuthLayout>
      {stage === 'email' && (
        <form onSubmit={sendCode}>
          <h1>Can’t sign in?</h1>
          <p className="muted">We’ll email you a code. You’ll also need one of your backup codes, or a code from your church administrator.</p>
          {error && <Notice tone="error">{error}</Notice>}
          <TextField label="Your email address" type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} required autoFocus />
          <Actions>
            <Button type="submit" busy={busy} busyLabel="Sending…">Email me a code</Button>
            <Link to="/sign-in">Back to sign in</Link>
          </Actions>
        </form>
      )}
      {stage === 'proof' && (
        <form onSubmit={checkProof}>
          <h1>Check your email</h1>
          <p className="muted">If that address has an account here, we’ve sent it a code. It lasts ten minutes.</p>
          {error && <Notice tone="error">{error}</Notice>}
          <TextField label="The code from the email" inputMode="numeric" autoComplete="one-time-code" value={emailCode} onChange={(e) => setEmailCode(e.target.value)} required autoFocus />
          <FieldGroup legend="And one of these">
            <CheckField type="radio" name="proof" label="One of my backup codes" checked={proofKind === 'backup'} onChange={() => chooseProof('backup')} />
            <CheckField type="radio" name="proof" label="A code from my church administrator" checked={proofKind === 'administrator'} onChange={() => chooseProof('administrator')} />
          </FieldGroup>
          <TextField
            label={proofKind === 'backup' ? 'Backup code' : 'Code from your administrator'}
            autoComplete="off"
            value={proof}
            onChange={(e) => setProof(e.target.value)}
            required
            {...(proofKind === 'administrator' ? { hint: 'Don’t have one? Ask your church administrator to help you sign in again. They’ll find this under My sign-in.' } : {})}
          />
          <Actions>
            <Button type="submit" busy={busy} busyLabel="Checking…">Continue</Button>
            <Button variant="quiet" onClick={() => startAgain('')}>Start again</Button>
          </Actions>
        </form>
      )}
      {stage === 'choose' && (
        <div>
          <h1>What do you need to replace?</h1>
          <p className="muted">Replace either or both, then sign in. You have fifteen minutes.</p>
          {changed.length > 0 && <Notice>Done: {changed.join(' and ')}.</Notice>}
          <Actions>
            <Button variant="quiet" onClick={() => setStage('password')}>My password</Button>
            <Button variant="quiet" onClick={() => setStage('authenticator')}>My authenticator app</Button>
          </Actions>
          <Actions><Button onClick={finish}>{changed.length ? 'Finish' : 'Nothing, take me to sign in'}</Button></Actions>
        </div>
      )}
      {stage === 'password' && grantId && <PasswordStep grantId={grantId} onExpired={() => startAgain()} onDone={() => { setChanged((c) => [...c, 'your new password']); setStage('choose'); }} />}
      {stage === 'authenticator' && grantId && <AuthenticatorStep grantId={grantId} onExpired={() => startAgain()} onConfirmed={(c) => { setCodes(c); setChanged((x) => [...x, 'your new authenticator app and backup codes']); setStage('codes'); }} />}
      {stage === 'codes' && <BackupCodesStep codes={codes} onDone={() => setStage('choose')} />}
    </AuthLayout>
  );
}
